import { useEffect, useRef, useState } from 'react'
import {
  MAX_WAIT_MS,
  pollDelayFor,
  type PollSchedule,
} from '../components/widgets/polling'

interface UseWaitPollOptions<T> {
  fetchStatus: () => Promise<T>
  schedule: PollSchedule
  resolved: boolean
  onSettled: (status: T, timedOut: boolean) => void
  restartKey?: string
}

export interface WaitPoll<T> {
  status: T | null
  timedOut: boolean
  loadFailed: boolean
  retry: () => void
}

export const useWaitPoll = <T extends { pending: boolean }>({
  fetchStatus,
  schedule,
  resolved,
  onSettled,
  restartKey = '',
}: UseWaitPollOptions<T>): WaitPoll<T> => {
  // Never reset: a refused report reopens the card, and re-sending on every
  // poll would loop.
  const reported = useRef(false)
  // The host hands fresh callbacks every render; keying the poll on them would
  // restart the wait each time the chat re-renders.
  const fetchStatusRef = useRef(fetchStatus)
  fetchStatusRef.current = fetchStatus
  const onSettledRef = useRef(onSettled)
  onSettledRef.current = onSettled

  const [status, setStatus] = useState<T | null>(null)
  const [timedOut, setTimedOut] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const startedAt = Date.now()
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const settle = (next: T, late: boolean) => {
      if (late) setTimedOut(true)
      if (resolved || reported.current) return
      reported.current = true
      onSettledRef.current(next, late)
    }

    const poll = async () => {
      let next: T
      try {
        next = await fetchStatusRef.current()
      } catch {
        if (!cancelled) setLoadFailed(true)
        return
      }
      if (cancelled) return

      setStatus(next)
      // A card reopened from history shows where things stand now, once.
      if (resolved) return

      const elapsedMs = Date.now() - startedAt
      if (!next.pending || elapsedMs >= MAX_WAIT_MS) {
        settle(next, next.pending)
        return
      }

      timer = setTimeout(() => void poll(), pollDelayFor(elapsedMs, schedule))
    }

    setLoadFailed(false)
    void poll()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [attempt, resolved, restartKey, schedule])

  return {
    status,
    timedOut,
    loadFailed,
    retry: () => setAttempt((previous) => previous + 1),
  }
}
